// Appearance runtime: reads the stored palette/mode, paints it onto <html>,
// and persists it locally. Kept free of React so the module-level apply below
// runs as early as the bundle loads.
//
// The DOM contract is two attributes on <html> — `data-theme-palette` and
// `data-theme` — which `src/styles/palettes.css` selects on, and which the
// inline bootstrap in `index.html` also sets before first paint. That
// bootstrap intentionally duplicates `readAppearance` in a few lines of plain
// JS: it has to run before the bundle to avoid a flash of the wrong theme.
// Keep the two in sync — `appearance.spec.ts` pins the storage key and shape.

import {
  DEFAULT_THEME_MODE,
  DEFAULT_THEME_PALETTE,
  isThemeMode,
  isThemePalette,
  type ThemeMode,
  type ThemePalette,
} from '@wms/shared';

export const APPEARANCE_STORAGE_KEY = 'wms.appearance';

export interface Appearance {
  themePalette: ThemePalette;
  themeMode: ThemeMode;
}

export const DEFAULT_APPEARANCE: Appearance = {
  themePalette: DEFAULT_THEME_PALETTE,
  themeMode: DEFAULT_THEME_MODE,
};

/** Device-local appearance. Falls back to defaults field by field. */
export function readAppearance(): Appearance {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) return DEFAULT_APPEARANCE;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_APPEARANCE;
    const value = parsed as Partial<Appearance>;
    return {
      themePalette: isThemePalette(value.themePalette)
        ? value.themePalette
        : DEFAULT_APPEARANCE.themePalette,
      themeMode: isThemeMode(value.themeMode) ? value.themeMode : DEFAULT_APPEARANCE.themeMode,
    };
  } catch {
    // Private mode / storage disabled — run session-only rather than crash.
    return DEFAULT_APPEARANCE;
  }
}

/**
 * The OS colour-scheme query, or null where the host has no `matchMedia`
 * (jsdom under test, older embedded webviews). Callers treat null as "the OS
 * has no opinion" and fall back to light.
 */
export function prefersDarkQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(prefers-color-scheme: dark)');
}

/** Collapses 'system' to what the OS is actually asking for right now. */
export function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return prefersDarkQuery()?.matches ? 'dark' : 'light';
}

/** Paints onto <html>, persists locally, and returns the resolved light/dark. */
export function applyAppearance(appearance: Appearance): 'light' | 'dark' {
  const resolved = resolveMode(appearance.themeMode);
  const root = document.documentElement;
  root.dataset.themePalette = appearance.themePalette;
  root.dataset.theme = resolved;
  // Keeps form controls, scrollbars and the caret in step with the palette.
  root.style.colorScheme = resolved;
  try {
    localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    // Session-only; the account-level copy still persists server-side.
  }
  return resolved;
}

// Applied at import time so the attributes exist before React's first render,
// even in contexts the `index.html` bootstrap does not cover (tests, SSR-less
// embeds). The bootstrap still matters in the browser: it runs before the
// bundle and so before first paint.
applyAppearance(readAppearance());
